import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import {
  AppLayout,
  TopNavigation,
  Container,
  Header,
  SpaceBetween,
  Button,
  Spinner
} from '@cloudscape-design/components';
import { PhotoGrid, DownloadAllButton } from './components/PhotoGrid';
import { NotFound } from './components/NotFound';
import { useStrings, useFolderInfo, useDefaultFolderId } from './hooks/useConfig';

const FolderView: React.FC = () => {
  const { folderId, fileId } = useParams<{ folderId: string; fileId?: string }>();
  const { strings, loading: stringsLoading } = useStrings();
  const { folderInfo, loading: folderLoading } = useFolderInfo(folderId || '');

  // Validate folderId exists
  if (!folderId) {
    return <NotFound />;
  }

  // Show loading while configs are being fetched
  if (stringsLoading || folderLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  // Fallback values if config fails to load
  const appTitle = strings?.app.title || 'Arcloud Photos';
  const refreshText = strings?.navigation.refresh || 'Refresh';
  const folderDisplayName = folderInfo?.displayName || 'Photo Gallery';
  const folderDescription = folderInfo?.description || 'Browse photos from Arcloud\'s Box collection';

  return (
    <div>
      <TopNavigation
        identity={{
          href: '/',
          title: appTitle
        }}
      />
      
      <AppLayout
        navigationHide={true}
        toolsHide={true}
        content={
          <SpaceBetween direction="vertical" size="l">
            <Container
              header={
                <Header
                  variant="h1"
                  description={folderDescription}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <DownloadAllButton folderId={folderId} />
                      <Button
                        onClick={() => window.location.reload()}
                        iconName="refresh"
                      >
                        {refreshText}
                      </Button>
                    </SpaceBetween>
                  }
                >
                  {folderDisplayName}
                </Header>
              }
            >
              <PhotoGrid folderId={folderId} fileId={fileId} />
            </Container>
          </SpaceBetween>
        }
      />
    </div>
  );
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { defaultFolderId, loading } = useDefaultFolderId();
  
  useEffect(() => {
    if (defaultFolderId && !loading) {
      navigate(`/${defaultFolderId}`);
    }
  }, [defaultFolderId, loading, navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  return null;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/:folderId" element={<FolderView />} />
        <Route path="/:folderId/:fileId" element={<FolderView />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
export {};